import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, Fragment as _Fragment, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeStyle as _normalizeStyle, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["textContent"]
const _hoisted_2 = ["textContent"]
const _hoisted_3 = { class: "col-2" }
const _hoisted_4 = { class: "text-xs text-gray-500 mx-1 flex items-center" }
const _hoisted_5 = {
  key: 1,
  class: "file-actions"
}

import { FileInterface } from '@/types';
import { EMIT } from '@/enum';
import { computed, onMounted, onBeforeUnmount, toRefs, ref, watch } from 'vue';
import { useMessageCenterStore } from '@/store';
import { storeToRefs } from 'pinia';
import AudioMessage from '@/components/MessageCenter/components/Chat/components/AudioMessage.vue';
import MessageActions from '@/components/MessageCenter/components/Chat/components/MessageActions';
import { mimeFromExt } from '@/composables';

interface Props {
  file: FileInterface;
  tag?: string;
  messageId?: number;
  createdAt: Date | string;
  documentId?: number;
  isYou?: boolean;
  previewFile?: boolean;
  metadata?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PreviewMessageFile',
  props: {
    file: {},
    tag: { default: 'li' },
    messageId: { default: undefined },
    createdAt: {},
    documentId: { default: undefined },
    isYou: { type: Boolean },
    previewFile: { type: Boolean, default: false },
    metadata: { default: undefined }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { createdAt, file, previewFile, tag, documentId, messageId, isYou } = toRefs(props);
const messageCenterStore = useMessageCenterStore();
const { attachmentFile } = storeToRefs(messageCenterStore);
const { getFileTypes } = toRefs(messageCenterStore);

const blobUrl = ref<string | null>(null);
const isLocalFile = ref<boolean>(false);

const fileTypeIcon = computed(() => {
  let ext = '';
  if (file.value?.file && (file.value.file as Blob).type) {
    const mime = (file.value.file as Blob).type;
    const mimeParts = mime.split('/');
    ext = mimeParts[mimeParts.length - 1];
    ext = (file.value.file as File)?.name?.split('.').pop()?.toLowerCase() || ext;
  } else if (file.value?.name) {
    ext = file.value.name.split('.').pop()?.toLowerCase() || '';
  }

  const iconType = getFileTypes.value.find((type) => type.ext.toLowerCase() === ext);
  return iconType ? iconType.icon : 'insert_drive_file';
});

const fileSize = computed(() => {
  if (!file.value || !file.value.file) return '0.00 MB';
  return ((file.value.file as Blob).size / 1024 / 1024).toFixed(2) + ' MB';
});

const isAudioFile = computed(() => {
  const audioExtensions = getFileTypes.value.filter((type) => type.category === 'audio').map((type) => type.ext);
  const extension = file.value.name.split('.').pop()?.toLowerCase();
  return audioExtensions.includes(extension || '');
});

const guessAudioMime = (name?: string) => {
  const ext = name?.split('.').pop()?.toLowerCase();
  return mimeFromExt(ext).mime;
};

const toBlob = (input: Blob | ArrayBuffer, typeHint?: string) => {
  if (input instanceof Blob) return input;

  if (input instanceof ArrayBuffer) return new Blob([new Uint8Array(input)], { type: typeHint });
  if (input?.buffer && input?.byteLength !== undefined) return new Blob([input], { type: typeHint });

  return new Blob([input], { type: typeHint || 'application/octet-stream' });
};

const setBlobUrlFrom = (data: Blob | ArrayBuffer, mime?: string) => {
  const next = toBlob(data, mime);
  const oldUrl = blobUrl.value;
  blobUrl.value = URL.createObjectURL(next);
  if (oldUrl) URL.revokeObjectURL(oldUrl);
};

const loadAndPreview = async (local?: Blob | File) => {
  if (local && !isAudioFile.value) {
    isLocalFile.value = true;
    emit(EMIT.SHOW_PREVIEW, local);
    return;
  }

  if (messageId.value) await messageCenterStore.loadMessageFile(messageId.value, file.value);

  if (attachmentFile.value && !isAudioFile.value) {
    const type = attachmentFile.value.type || 'application/octet-stream';
    const blob = new Blob([attachmentFile.value.content], { type });
    emit(EMIT.SHOW_PREVIEW, blob);
    return;
  }

  if (isAudioFile.value) {
    isLocalFile.value = true;
    const mime = (local as Blob)?.type || attachmentFile.value?.type || guessAudioMime(file.value?.name);
    const content = local ?? attachmentFile.value?.content;
    if (content) setBlobUrlFrom(content, mime);
  }
};

onMounted(() => {
  if (blobUrl.value) blobUrl.value = null;
});

onBeforeUnmount(() => {
  if (blobUrl.value) URL.revokeObjectURL(blobUrl.value);
});
const onClickPreview = () => {
  blobUrl.value = null;
  if (file.value?.file instanceof Blob) {
    isLocalFile.value = true;
    loadAndPreview(file.value.file);
  } else {
    loadAndPreview();
  }
};

watch(
  () => [file.value?.file, attachmentFile.value],
  (f) => {
    if (attachmentFile.value === undefined) {
      blobUrl.value = null;
      return;
    }
    if (isAudioFile.value && f instanceof Blob) {
      const mime = f.type || guessAudioMime(file.value?.name);
      const audioBlob = toBlob(f, mime);
      blobUrl.value = null;
      blobUrl.value = URL.createObjectURL(audioBlob);
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(tag)), {
    class: "file-preview relative",
    style: _normalizeStyle(_unref(isYou) ? 'padding-top: 1rem;' : '')
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass([_unref(previewFile) ? 'col-10' : 'col-6', 'flex', 'align-items-center'])
      }, [
        (!isAudioFile.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "box-preview",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (onClickPreview()))
            }, [
              _createElementVNode("span", {
                class: "material-icons drive mx-1",
                textContent: _toDisplayString(fileTypeIcon.value)
              }, null, 8, _hoisted_1),
              _createElementVNode("span", {
                class: "title font-light my-0 mx-1 cursor-pointer",
                textContent: _toDisplayString(_unref(file).name)
              }, null, 8, _hoisted_2)
            ]))
          : _createCommentVNode("", true),
        (isAudioFile.value)
          ? (_openBlock(), _createBlock(AudioMessage, {
              key: _unref(file).name,
              src: blobUrl.value,
              "is-for-preview": true,
              "meta-duration": __props.metadata ? JSON.parse(__props.metadata)?.duration : null,
              onPlay: onClickPreview
            }, null, 8, ["src", "meta-duration"]))
          : _createCommentVNode("", true)
      ], 2),
      (!_unref(previewFile))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("span", _hoisted_4, "(" + _toDisplayString(fileSize.value) + ")", 1)
            ]),
            _createElementVNode("div", {
              class: "close absolute right-0 my-0 mx-1 cursor-pointer",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (emit(_unref(EMIT).REMOVE, _unref(file))))
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("span", { class: "material-icons" }, "delete", -1)
            ]))])
          ], 64))
        : (_unref(isYou))
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(MessageActions), {
                "created-at": _unref(createdAt),
                "message-id": _unref(messageId),
                "document-id": _unref(documentId),
                action: _unref(EMIT).DELETE_MESSAGE_FILE
              }, null, 8, ["created-at", "message-id", "document-id", "action"])
            ]))
          : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})